export const PLAN_STARTER = "starter";
export const PLAN_STANDARD = "standard";
export const PLAN_PRO = "pro";
export const PLAN_ENTERPRISE = "enterprise";

export const CANONICAL_PLAN_IDS = Object.freeze([
  PLAN_STARTER,
  PLAN_STANDARD,
  PLAN_PRO,
  PLAN_ENTERPRISE,
]);

export const PLAN_LABELS = Object.freeze({
  [PLAN_STARTER]: "Starter",
  [PLAN_STANDARD]: "Standard",
  [PLAN_PRO]: "Pro",
  [PLAN_ENTERPRISE]: "Enterprise",
});

export const PLAN_SEAT_LIMITS = Object.freeze({
  [PLAN_STARTER]: 1,
  [PLAN_STANDARD]: 3,
  [PLAN_PRO]: 10,
  [PLAN_ENTERPRISE]: null,
});

export const PLAN_REFERENCE_LIMITS = Object.freeze({
  [PLAN_STARTER]: 100,
  [PLAN_STANDARD]: 100,
  [PLAN_PRO]: null,
  [PLAN_ENTERPRISE]: null,
});

const FAIL_CLOSED_ENTITLEMENTS = Object.freeze({
  seatLimit: 0,
  referenceLimit: 0,
  messagingEnabled: false,
  aiCitationEnabled: false,
  automatedRefBlocksEnabled: false,
  smartAnnotationsEnabled: false,
  ssoEnabled: false,
  brandingEnabled: false,
});

const ENTITLEMENTS_BY_PLAN = Object.freeze({
  [PLAN_STARTER]: Object.freeze({
    seatLimit: 1,
    referenceLimit: 100,
    messagingEnabled: false,
    aiCitationEnabled: false,
    automatedRefBlocksEnabled: false,
    smartAnnotationsEnabled: false,
    ssoEnabled: false,
    brandingEnabled: false,
  }),
  [PLAN_STANDARD]: Object.freeze({
    seatLimit: 3,
    referenceLimit: 100,
    messagingEnabled: true,
    aiCitationEnabled: false,
    automatedRefBlocksEnabled: false,
    smartAnnotationsEnabled: false,
    ssoEnabled: false,
    brandingEnabled: false,
  }),
  [PLAN_PRO]: Object.freeze({
    seatLimit: 10,
    referenceLimit: null,
    messagingEnabled: true,
    aiCitationEnabled: true,
    automatedRefBlocksEnabled: true,
    smartAnnotationsEnabled: true,
    ssoEnabled: false,
    brandingEnabled: false,
  }),
  [PLAN_ENTERPRISE]: Object.freeze({
    seatLimit: null,
    referenceLimit: null,
    messagingEnabled: true,
    aiCitationEnabled: true,
    automatedRefBlocksEnabled: true,
    smartAnnotationsEnabled: true,
    ssoEnabled: true,
    brandingEnabled: true,
  }),
});

export const FEATURE_LOCK_COPY = Object.freeze({
  messaging: Object.freeze({
    title: "Messaging is not available on your current plan",
    description:
      "Upgrade to Standard or Pro to unlock team collaboration and inbox messaging.",
    billingCtaLabel: "Open Billing",
  }),
  aiCitation: Object.freeze({
    title: "AI citation generation is not available on your current plan",
    description:
      "Upgrade to Pro to generate citation drafts automatically from your reference PDFs.",
    billingCtaLabel: "Open Billing",
  }),
  automatedRefBlocks: Object.freeze({
    title: "Automated ref blocks are not available on your current plan",
    description:
      "Upgrade to Pro to keep linked asset ref blocks synced when claim reference order changes.",
    billingCtaLabel: "Open Billing",
  }),
  smartAnnotations: Object.freeze({
    title: "Smart annotations are not available on your current plan",
    description:
      "Upgrade to Pro to auto-fill page, column, paragraph, and line metadata on new annotations.",
    billingCtaLabel: "Open Billing",
  }),
  referenceQuota: Object.freeze({
    title: "Reference limit reached",
    description:
      "Your organization has reached the total reference limit for your current plan. Delete references or upgrade to Pro for unlimited references.",
    billingCtaLabel: "Open Billing",
  }),
});

export function resolveCanonicalPlanId(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return CANONICAL_PLAN_IDS.includes(normalized) ? normalized : null;
}

export function getPlanLabel(value, fallback = "Unknown") {
  const planId = resolveCanonicalPlanId(value);
  return (planId && PLAN_LABELS[planId]) || fallback;
}

export function getFeatureLockCopy(featureKey, overrides = {}) {
  const base =
    FEATURE_LOCK_COPY[String(featureKey || "").trim()] ||
    FEATURE_LOCK_COPY.aiCitation;
  const safeOverrides = {};
  if (overrides && typeof overrides === "object") {
    Object.entries(overrides).forEach(([key, value]) => {
      if (value !== undefined) {
        safeOverrides[key] = value;
      }
    });
  }
  if (
    String(featureKey || "").trim() === "referenceQuota" &&
    safeOverrides.description === undefined
  ) {
    const limit = Number(safeOverrides.referenceLimit);
    if (Number.isFinite(limit) && limit > 0) {
      safeOverrides.description = `Your organization has reached the total reference limit of ${limit} references across active and archived items. Delete references or upgrade to Pro for unlimited references.`;
    }
  }
  return {
    ...base,
    ...safeOverrides,
  };
}

export function createFeatureLockedError(featureKey, overrides = {}) {
  const copy = getFeatureLockCopy(featureKey, overrides);
  const error = new Error(copy.description || "Feature not available.");
  error.code = "FEATURE_LOCKED";
  error.featureKey = String(featureKey || "").trim() || null;
  error.featureTitle = copy.title || "";
  return error;
}

export function isFeatureLockedError(error, featureKey = null) {
  if (!error || error.code !== "FEATURE_LOCKED") return false;
  if (!featureKey) return true;
  return String(error.featureKey || "") === String(featureKey);
}

export function getPlanEntitlements(planTier, options = {}) {
  const canonicalPlanId = resolveCanonicalPlanId(planTier);
  const base = canonicalPlanId
    ? ENTITLEMENTS_BY_PLAN[canonicalPlanId]
    : FAIL_CLOSED_ENTITLEMENTS;
  const seatsAllowed =
    typeof options.seatsAllowed === "number" &&
    Number.isFinite(options.seatsAllowed)
      ? options.seatsAllowed
      : null;
  const seatLimit =
    seatsAllowed !== null && seatsAllowed > 0 ? seatsAllowed : base.seatLimit;

  return {
    planTier: canonicalPlanId,
    seatLimit,
    referenceLimit: base.referenceLimit,
    messagingEnabled: Boolean(base.messagingEnabled),
    aiCitationEnabled: Boolean(base.aiCitationEnabled),
    automatedRefBlocksEnabled: Boolean(base.automatedRefBlocksEnabled),
    smartAnnotationsEnabled: Boolean(base.smartAnnotationsEnabled),
    ssoEnabled: Boolean(base.ssoEnabled),
    brandingEnabled: Boolean(base.brandingEnabled),
  };
}

export function normalizeEntitlements(
  raw,
  fallbackPlanTier = null,
  options = {},
) {
  const rawValue =
    raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
  const fallback = getPlanEntitlements(fallbackPlanTier, options);

  if (!rawValue) {
    return fallback;
  }

  const planTier =
    resolveCanonicalPlanId(rawValue.planTier) || fallback.planTier || null;
  const normalized = getPlanEntitlements(planTier, {
    seatsAllowed:
      typeof rawValue.seatLimit === "number"
        ? rawValue.seatLimit
        : options.seatsAllowed,
  });

  return {
    ...normalized,
    seatLimit:
      typeof rawValue.seatLimit === "number"
        ? rawValue.seatLimit
        : normalized.seatLimit,
    referenceLimit:
      rawValue.referenceLimit === null ||
      typeof rawValue.referenceLimit === "number"
        ? rawValue.referenceLimit
        : normalized.referenceLimit,
    messagingEnabled:
      typeof rawValue.messagingEnabled === "boolean"
        ? rawValue.messagingEnabled
        : normalized.messagingEnabled,
    aiCitationEnabled:
      typeof rawValue.aiCitationEnabled === "boolean"
        ? rawValue.aiCitationEnabled
        : normalized.aiCitationEnabled,
    automatedRefBlocksEnabled:
      typeof rawValue.automatedRefBlocksEnabled === "boolean"
        ? rawValue.automatedRefBlocksEnabled
        : normalized.automatedRefBlocksEnabled,
    smartAnnotationsEnabled:
      typeof rawValue.smartAnnotationsEnabled === "boolean"
        ? rawValue.smartAnnotationsEnabled
        : normalized.smartAnnotationsEnabled,
    ssoEnabled:
      typeof rawValue.ssoEnabled === "boolean"
        ? rawValue.ssoEnabled
        : normalized.ssoEnabled,
    brandingEnabled:
      typeof rawValue.brandingEnabled === "boolean"
        ? rawValue.brandingEnabled
        : normalized.brandingEnabled,
  };
}

export function hasUnlimitedReferences(entitlements) {
  return entitlements?.referenceLimit === null;
}

export function isReferenceLimitReached(entitlements, totalReferenceCount) {
  const limit = entitlements?.referenceLimit;
  if (limit === null || limit === undefined) return false;
  const count = Number(totalReferenceCount);
  if (!Number.isFinite(count)) return false;
  return count >= limit;
}
